/**
 * stripFields — FORM-strip (Fix-Wave-7)
 *
 * Removes named fields from a form data object before sending to a strict
 * Zod schema. Used by ROROS pages where the form includes a `domain_id`
 * picker for UX (the user selects a domain), but the actual API route takes
 * `domainId` from the URL path and the request body must NOT include
 * `domain_id` (Zod .strict() rejects unknown keys).
 *
 * Rule: any form submission to a .strict() Zod schema MUST go through
 * stripFields() before mutate() — never pass `data as any` directly.
 *
 * @example
 * const { domain_id, ...rest } = stripFields(data, ['domain_id']);
 * const domainId = (domain_id as string) || activeDomainId;
 * createMutation.mutate({ domainId, payload: rest });
 */

/**
 * Remove the given field keys from a record. Returns a new object with
 * those keys omitted. Type-safe: the return type excludes the stripped keys.
 */
export function stripFields<T extends Record<string, unknown>, K extends keyof T>(
  data: T,
  fields: K[],
): Omit<T, K> {
  const out = { ...data } as Record<string, unknown>;
  for (const f of fields) {
    delete out[f as string];
  }
  return out as Omit<T, K>;
}

/**
 * Convenience overload: strip a single 'domain_id' field and return it
 * alongside the rest. The most common pattern in ROROS artifact forms.
 */
export function stripDomainId(data: Record<string, unknown>): {
  domain_id: string | undefined;
  rest: Record<string, unknown>;
} {
  const { domain_id, ...rest } = data;
  return { domain_id: domain_id as string | undefined, rest };
}
